import {
  useButtonPressedCallback,
  useLinkPressedCallback
} from '@zenbusiness/application-commons-clickstream';
import Checkbox from '@zenbusiness/zui/Checkbox';
import Link from '@zenbusiness/zui/Link';
import Text from '@zenbusiness/zui/Text';
import PropTypes from 'prop-types';

import useConst from '#client/hooks/useConst';

import { FormControlLabelStyled } from './components/UserAgreementsUIField.styles';

const E_SIGN_AUTHORIZATION_URL = 'https://www.zenbusiness.com/banking-esign/';
const DEPOSIT_ACCOUNT_AGREEMENT_URL =
  'https://disclosures.thread.bank/terms/zenbusinessinc/businessdepositaccountagreement.pdf';
const ACCOUNT_DISCLOSURES_URL =
  'https://disclosures.thread.bank/terms/zenbusinessinc/businessdepositaccountagreementdisclosures.pdf';
const DEBIT_CARD_HOLDER_AGREEMENT_URL =
  'https://disclosures.thread.bank/terms/zenbusinessinc/businessdebitcardholderagreement.pdf';
const PRIVACY_POLICY_URL = 'https://www.zenbusiness.com/support-privacy-policy/';
const TERMS_OF_USE_URL = 'https://www.zenbusiness.com/support-terms/';

export const UserAgreementsUIField = ({
  field = { setSaved: () => {}, setLoading: () => {} },
  value = false,
  dataChangeHandler = () => {},
  disabled
}) => {
  const defaultChecked = useConst(value ?? false);
  const trackUserAgreementChangeClick = useButtonPressedCallback({
    elementText: 'user_agreements',
    elementName: 'banking_collect_info',
    appPageName: 'banking_application'
  });
  const onDataChanged = (event, checked) => {
    if (checked) {
      field.setLoading();
    }

    dataChangeHandler(checked, { agreements: checked }, (forceUpdate, { isSave, isError }) => {
      field.setSaved(isSave, isError, checked);
      forceUpdate();
    });
    /**
     * Tracking Details: Tracking to be added to the banking application Collect Info screen.
     * Question:
     *          Where are customers typically dropping off on this screen?
     *          Do they typically drop off at SSN?
     *          How often are they viewing the user agreements?
     */
    trackUserAgreementChangeClick();
  };

  const trackEsignAuthorizationClick = useLinkPressedCallback({
    elementName: 'banking_esign_authorization',
    destination: E_SIGN_AUTHORIZATION_URL
  });

  const trackDepositAccountAgreementClick = useLinkPressedCallback({
    elementName: 'banking_deposit_account_agreement',
    destination: DEPOSIT_ACCOUNT_AGREEMENT_URL
  });

  const trackAccountDisclosuresClick = useLinkPressedCallback({
    elementName: 'banking_account_disclosures',
    destination: ACCOUNT_DISCLOSURES_URL
  });

  const trackDebitCardHolderAgreementClick = useLinkPressedCallback({
    elementName: 'banking_debit_card_holder_agreement',
    destination: DEBIT_CARD_HOLDER_AGREEMENT_URL
  });

  const trackPrivacyPolicyClick = useLinkPressedCallback({
    elementName: 'banking_privacy_policy',
    destination: PRIVACY_POLICY_URL
  });

  const trackTermsOfUseClick = useLinkPressedCallback({
    elementName: 'banking_terms_of_use',
    destination: TERMS_OF_USE_URL
  });

  return (
    <FormControlLabelStyled
      control={
        <Checkbox
          color="default"
          defaultChecked={defaultChecked}
          disabled={disabled}
          id="agreements"
          inputRef={field.ref}
          onChange={onDataChanged}
        />
      }
      label={
        <Text
          aria-label="Required agreements"
          color="primary"
          size="md"
        >
          I have read and accept the{' '}
          <Link
            color="info"
            href={E_SIGN_AUTHORIZATION_URL}
            onClick={trackEsignAuthorizationClick}
            size="md"
            target="_blank"
            underline="none"
            weight="medium"
          >
            Banking E-sign Authorization
          </Link>
          {', '}
          <Link
            color="info"
            href={DEPOSIT_ACCOUNT_AGREEMENT_URL}
            onClick={trackDepositAccountAgreementClick}
            size="md"
            target="_blank"
            underline="none"
            weight="medium"
          >
            Deposit Account Agreement
          </Link>
          {', '}
          <Link
            color="info"
            href={ACCOUNT_DISCLOSURES_URL}
            onClick={trackAccountDisclosuresClick}
            size="md"
            target="_blank"
            underline="none"
            weight="medium"
          >
            Account Disclosures
          </Link>
          {', '}
          <Link
            color="info"
            href={DEBIT_CARD_HOLDER_AGREEMENT_URL}
            onClick={trackDebitCardHolderAgreementClick}
            size="md"
            target="_blank"
            underline="none"
            weight="medium"
          >
            Debit Card Holder Agreement
          </Link>
          {', '}
          <Link
            color="info"
            href={PRIVACY_POLICY_URL}
            onClick={trackPrivacyPolicyClick}
            size="md"
            target="_blank"
            underline="none"
            weight="medium"
          >
            Privacy Policy
          </Link>
          {', '}
          and the{' '}
          <Link
            color="info"
            href={TERMS_OF_USE_URL}
            onClick={trackTermsOfUseClick}
            size="md"
            target="_blank"
            underline="none"
            weight="medium"
          >
            Terms of Use
          </Link>
          .
        </Text>
      }
    />
  );
};

UserAgreementsUIField.propTypes = {
  dataChangeHandler: PropTypes.func,
  field: PropTypes.shape({
    setSaved: PropTypes.func,
    setLoading: PropTypes.func,
    // eslint-disable-next-line react/forbid-prop-types
    ref: PropTypes.shape({ current: PropTypes.any })
  }),
  value: PropTypes.bool,
  disabled: PropTypes.bool
};
